import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import countryList from "react-select-country-list";

import { savePlant, editPlant, getPlantDetails } from "../../network/cipAPI";
import {
  storeUnits,
  storeAppLanguage,
} from "../../Store/TemplateStore/template.action";

import Header from "../../components/Header";
import ErrorLabel from "../../components/ErrorLabel";
import InputBox from "../../components/InputBox";
import Dropdown from "../../components/Dropdown";
import DupontButton from "../../components/DupontButton";
import PageLoading from "../../components/PageLoading";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import Settings from "../home/Settings";

import {
  getChangedData,
  extractPlantId,
  updatePlantId,
  getSelectedPlantTrainInfo,
} from "../../utils/Utils";
import { saveItemSession } from "../../utils/session";
import DupontCheckbox from "../../components/DupontCheckbox";
import { PLANT_DETAILS } from "./addNewPlant.config";
import { WATER_TYPE } from "./addNewPlant.config";
import RoConfiguration from "./roConfiguration";
import { API_URLS } from "../../network/appUrls";
import "./index.scss";

//const isLocalhost = window.location.hostname === "localhost";
//const basePath = isLocalhost ? "/" : "/roadvisor/";
const basePath = API_URLS.basePath;

const AddNewPlant = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [noOfTrains, setNoOfTrains] = useState(location.state?.noOfTrains);
  const accessUserPlant = useSelector((state) => state.accessUserPlant);

  let countryOptions = useMemo(() => countryList().getData(), []);
  countryOptions = countryOptions.map((item) => item.label);
  const [waterTypeOptions, setWaterTypeOptions] = useState(WATER_TYPE(t));

  const searchParams = new URLSearchParams(location.search);
  const isEdit = searchParams.get("isEdit");
  const plantId = searchParams.get("plantId");

  const token = useSelector((state) => state.token);
  const [plantDetail, setPlantDetail] = useState([]);
  const [plantDetailerror, setPlantDetailError] = useState([]);
  const [country, setCountry] = useState("");
  const [waterType, setWaterType] = useState("");
  const [trains, setTrains] = useState([]);
  const [totalNumberOfElements, setTotalNumberOfElements] = useState("");
  const [stageValidationSucessfull, setStageValidationSucessfull] =
    useState(true);
  const [fetchedPlanDetails, setFetchedPlanDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dupontElement, setDupontElement] = useState(false);
  const {
    flow,
    pressure,
    feed_quality,
    feed_water_factor,
    permeate_quality,
    feed_water_permeate_factor,
    temperature,
    flux,
    volume,
    mass,
    currency,
    conversion_rate,
  } = useSelector((state) => state);
  const plantData = useSelector((state) => state.plantData);
  useEffect(() => {
    setWaterTypeOptions(WATER_TYPE(t));
  }, [t]);

  useEffect(() => {
    //console.log("plantId", plantId);
    //if (plantId && token) {
    if (token && isEdit && plantId) {
      const getPlantDetail = async () => {
        setIsLoading(true);
        try {
          const sel_train = getSelectedPlantTrainInfo(plantData, plantId);
          const response = await getPlantDetails(plantId, false, sel_train);
          if (response && response.data) {
            const detail = response.data;
            setFetchedPlanDetails(detail);
            setPlantDetail({
              ...plantDetail,
              ["installationName"]: detail.name,
              ["description"]: detail.description,
              ["city"]: detail.city,
            });
            setCountry(detail.country);
            setNoOfTrains(detail.num_trains);
            setDupontElement(detail.is_dupont_element);
            setWaterType(detail.water_type);
            setTrains(detail.config?.trains);
            dispatch(storeUnits(detail.setting));
          }
        } catch (error) {
          console.log("getPlantDetail", error);
          setIsLoading(false);
        } finally {
          setIsLoading(false);
        }
      };
      getPlantDetail();
    }
  }, [token]);

  const changeCountry = (value) => {
    setCountry(value);
    setPlantDetailError({ ...plantDetailerror, ["country"]: "" });
  };
  const changeWaterType = (value) => {
    setWaterType(value);
    setPlantDetailError({ ...plantDetailerror, ["waterType"]: "" });
  };
  const handleCheckboxChange = (dupontElement, name) => {
    setDupontElement(dupontElement);
  };
  const handleInputChange = (key, value) => {
    setPlantDetail({ ...plantDetail, [key]: value });
    if (key === "installationName") {
      const error =
        value.trim() === "" ? t("addNewPlant.installationNameRequired") : "";
      setPlantDetailError({ ...plantDetailerror, [key]: error });
    } else {
      setPlantDetailError({ ...plantDetailerror, [key]: "" });
    }
  };

  const runValidation = () => {
    setStageValidationSucessfull(false);
    let validationSuccessful = true;
    const error = [];
    if (!plantDetail.installationName) {
      validationSuccessful = false;
      error["installationName"] = t("addNewPlant.installationNameRequired");
    }
    if (!country) {
      validationSuccessful = false;
      error["country"] = t("addNewPlant.countryRequired");
    }
    if (!waterType) {
      validationSuccessful = false;
      error["waterType"] = t("addNewPlant.waterTypeRequired");
    }
    setPlantDetailError({
      ...plantDetailerror,
      ...error,
    });
    for (const train of Object.values(trains)) {
      for (const pass of train.passes) {
        if (pass?.stages?.length === 0) {
          validationSuccessful = false;
          break;
        }

        for (const stage of pass.stages) {
          if (
            stage.num_pressure_vessels === undefined ||
            stage.num_element_per_pressure_vessel === undefined
          ) {
            validationSuccessful = false;
            break;
          }
        }
      }
    }
    return validationSuccessful;
  };

  const savePlantDetails = async (key) => {
    setIsLoading(true);
    try {
      const validated = runValidation();
      let response = null;
      if (validated) {
        const payload = {
          name: plantDetail.installationName,
          description: plantDetail.description,
          city: plantDetail.city,
          country: country,
          water_type: waterType,
          setting: {
            flow: flow,
            pressure: pressure,
            feed_quality: feed_quality,
            feed_water_factor: feed_water_factor,
            permeate_quality: permeate_quality,
            feed_water_permeate_factor: feed_water_permeate_factor,
            temperature: temperature,
            flux: flux,
            volume: volume,
            mass: mass,
            currency: currency,
            conversion_rate: conversion_rate,
          },
          config: {
            trains: [...Object.values(trains)],
          },
          tol_num_elements: totalNumberOfElements,
          is_dupont_element: dupontElement,
        };

        saveItemSession("water_type", waterType);
        saveItemSession("name", plantDetail.installationName);
        if (plantId && isEdit) {
          const editedFields = getChangedData(payload, fetchedPlanDetails);

          response = await editPlant(plantId, editedFields);
        } else {
          response = await savePlant(payload);
        }
        if (response.created_at) {
          if (key === "exit") {
            if (accessUserPlant) {
              dispatch({ type: "STORE_ACCESS_USER_PLANT", payload: false });
              navigate("/user-management");
            } else {
              navigate(-1);
            }
            createNotification(
              SUCCESS_NOTIFICATION,
              t("addNewPlant.successfullyAdded"),
              ""
            );
          } else {
            updatePlantId(response.plant_id);
            let path = "";
            if (isEdit)
              path = `${basePath}analyseData?plantId=${response.plant_id}&isEdit=${isEdit}`;
            else path = `${basePath}analyseData?plantId=${response.plant_id}`;
            navigate(path);
          }
        } else {
          createNotification(ERROR_NOTIFICATION, response.detail.error, "");
        }
      }
    } catch (error) {
      console.log("savePlantDetails", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-main">
      <Header />

      <div className="main-container">
        {isLoading ? (
          <PageLoading customHeight={"515px"} />
        ) : (
          <div>
            <div className="calculatedValueText">
              {isEdit
                ? t("roAdvisorProject.editPlant")
                : t("roAdvisorProject.addPlant")}
            </div>
            <div className="grid-container-row">
              <InputBox
                name={"installationName"}
                value={plantDetail["installationName"]}
                labelText={t(`addNewPlant.${"installationName"}`)}
                placeholder={t(`addNewPlant.${"installationName"}`)}
                rows={1}
                isRequired={true}
                onChange={(value) =>
                  handleInputChange("installationName", value)
                }
                errorMsg={plantDetailerror["installationName"]}
                maxLength={250}
              />
              <InputBox
                name={"city"}
                value={plantDetail["city"]}
                labelText={t(`addNewPlant.${"city"}`)}
                placeholder={t(`addNewPlant.${"city"}`)}
                rows={1}
                isRequired={false}
                onChange={(value) => handleInputChange("city", value)}
                errorMsg={plantDetailerror["city"]}
                maxLength={100}
              />
            </div>
            <div className="grid-container-row marginTop5 marginBtm5">
              <Dropdown
                labelText={t("addNewPlant.country")}
                placeholder={t("addNewPlant.country")}
                value={country}
                onChange={changeCountry}
                options={countryOptions}
                errorMsg={plantDetailerror["country"]}
                isRequired
                showCancelIcon
              />
              <Dropdown
                labelText={t("addNewPlant.waterType")}
                placeholder={t("addNewPlant.waterType")}
                value={waterType}
                onChange={changeWaterType}
                options={waterTypeOptions}
                errorMsg={plantDetailerror["waterType"]}
                isRequired
                showCancelIcon
              />
            </div>
            <InputBox
              name={"description"}
              value={plantDetail["description"]}
              labelText={t(`addNewPlant.${"description"}`)}
              placeholder={t(`addNewPlant.${"description"}`)}
              rows={3}
              isRequired={false}
              onChange={(value) => handleInputChange("description", value)}
              errorMsg={plantDetailerror["description"]}
              maxLength={500}
            />

            <div>
              <div className="bodyMainHeadingText margin-top-20 flex-ro font-bold">
                {t("addNewPlant.reverseOsmosis")}
                {!stageValidationSucessfull && (
                  <ErrorLabel
                    className="margin-left-ro"
                    message={t("addNewPlant.mandatoryRequired")}
                  />
                )}
              </div>
              <div className="reverse-osmosis description-text ">
                <RoConfiguration
                  trains={trains}
                  setTrains={setTrains}
                  setTotalNumberOfElements={setTotalNumberOfElements}
                  stageValidationSucessfull={stageValidationSucessfull}
                  setStageValidationSucessfull={setStageValidationSucessfull}
                  noOfTrains={noOfTrains}
                />

                <div className="flex-between padding-top-20  ">
                  <DupontCheckbox
                    labelText={t("addNewPlant.nonDupontElement")}
                    checked={dupontElement}
                    onChange={handleCheckboxChange}
                  />
                  <div className="flex-between">
                    {t("addNewPlant.totalNoOfElements")}
                    <div className="padding-left-50">
                      <InputBox
                        value={totalNumberOfElements}
                        placeholder={t(`addNewPlant.totalNoOfElements`)}
                        disabled
                        hideClearBtn
                      />
                    </div>
                  </div>
                </div>
                <Settings disabled={false} />
              </div>
              <div className=" button-right-container padding-top-20 ">
                <DupontButton
                  customClass={"margin-bottom margin-left-10"}
                  onClick={() => savePlantDetails("exit")}
                >
                  {t("ValueStrings.saveExit")}
                </DupontButton>
                <DupontButton
                  customClass={"margin-bottom margin-left-10"}
                  onClick={() => savePlantDetails("continue")}
                >
                  {t("ValueStrings.saveContinue")}
                </DupontButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default AddNewPlant;
